import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';
import { Icon, Button } from '../ui';
import { NAV, LANGUAGES } from '../../data/nav';
import { changeAppLanguage } from '../../i18n';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../theme/ThemeContext';
import { trackLanguageSwitch, trackThemeToggle } from '../../utils/analytics';

export default function MobileDrawer({ open, onClose }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const { mode, toggleTheme } = useTheme();
  const [expanded, setExpanded] = useState(null);

  // Close on route change
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Lock body scroll while open + Escape to close
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  if (!open) return null;

  const setLang = (code) => { changeAppLanguage(code); trackLanguageSwitch(code); };

  return (
    <div className="drawer-root" role="dialog" aria-modal="true" aria-label={t('Menu')}>
      <div className="drawer__overlay" onClick={onClose} />
      <aside className="drawer">
        <div className="drawer__head">
          <Logo compact />
          <button type="button" className="icon-btn" aria-label={t('Close menu')} onClick={onClose}><Icon name="x" size={20} /></button>
        </div>

        <nav className="drawer__nav" aria-label={t('Menu')}>
          {NAV.map((item) => item.children ? (
            <div key={item.id} className={`drawer__group${expanded === item.id ? ' is-open' : ''}`}>
              <button type="button" className="drawer__link drawer__link--group" aria-expanded={expanded === item.id} onClick={() => setExpanded(expanded === item.id ? null : item.id)}>
                {t(item.label)}<Icon name="chevron-down" size={16} className="drawer__caret" />
              </button>
              <div className="drawer__sub">
                {item.children.map((c) => (
                  <NavLink key={c.to} to={c.to} className={({ isActive }) => `drawer__sublink${isActive ? ' is-active' : ''}`}>
                    <Icon name={c.icon} size={16} />{t(c.label)}
                  </NavLink>
                ))}
              </div>
            </div>
          ) : (
            <NavLink key={item.id} to={item.to} end={item.to === '/'} className={({ isActive }) => `drawer__link${isActive ? ' is-active' : ''}`}>
              {t(item.label)}
            </NavLink>
          ))}
        </nav>

        <div className="drawer__foot">
          <div className="drawer__langs" role="group" aria-label={t('Change language')}>
            {LANGUAGES.map((l) => (
              <button key={l.code} type="button" className={`chip${i18n.language === l.code ? ' is-active' : ''}`} onClick={() => setLang(l.code)}><span lang={l.code}>{l.label}</span></button>
            ))}
          </div>
          <button type="button" className="btn btn--ghost btn--block" onClick={() => { trackThemeToggle(mode === 'dark' ? 'light' : 'dark'); toggleTheme(); }}>
            <Icon name={mode === 'dark' ? 'sun' : 'moon'} size={16} />{mode === 'dark' ? t('Light mode') : t('Dark mode')}
          </button>
          {isAuthenticated ? (
            <div className="drawer__account">
              <Link to="/profile" className="btn btn--soft" style={{ flex: 1 }}><Icon name="user" size={16} />{t('Profile')}</Link>
              <button type="button" className="btn btn--ghost" onClick={() => { logout(); onClose(); navigate('/'); }}><Icon name="log-out" size={16} />{t('Sign out')}</button>
            </div>
          ) : (
            <Button block to="/login" icon={<Icon name="user" size={16} />} iconPosition="left">{t('My Account')}</Button>
          )}
        </div>
      </aside>
    </div>
  );
}
